import React, { useEffect, useId, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

function SelectItem() {
  return null;
}

function SelectTrigger() {
  return null;
}

function SelectValue() {
  return null;
}

function SelectContent({ children }) {
  return children;
}

function SelectGroup({ children }) {
  return children;
}

function SelectLabel() {
  return null;
}

function SelectSeparator() {
  return null;
}

function collectItems(children, items = []) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === SelectItem) {
      items.push({
        value: String(child.props.value ?? ""),
        label: getText(child.props.children),
        disabled: child.props.disabled,
      });
      return;
    }
    collectItems(child.props.children, items);
  });
  return items;
}

function getText(children) {
  return React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (React.isValidElement(child)) return getText(child.props.children);
      return "";
    })
    .join("")
    .trim();
}

function findChild(children, type) {
  let match = null;
  React.Children.forEach(children, (child) => {
    if (match || !React.isValidElement(child)) return;
    if (child.type === type) {
      match = child;
      return;
    }
    match = findChild(child.props.children, type);
  });
  return match;
}

function Select({
  value,
  defaultValue = "",
  onValueChange,
  disabled = false,
  name,
  required = false,
  children,
}) {
  const [uncontrolledValue, setUncontrolledValue] = useState(String(defaultValue || ""));
  const [inputValue, setInputValue] = useState("");
  const inputId = useId().replace(/:/g, "");
  const isControlled = value !== undefined;
  const selectedValue = String(isControlled ? value ?? "" : uncontrolledValue);
  const items = useMemo(() => collectItems(children), [children]);
  const selectedItem = items.find((item) => item.value === selectedValue);
  const selectedLabel = selectedItem?.label || "";
  const trigger = findChild(children, SelectTrigger);
  const valueElement = findChild(children, SelectValue);
  const triggerProps = trigger?.props || {};
  const placeholder = valueElement?.props?.placeholder || "Select…";
  const listId = `${inputId}-options`;

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedValue, selectedLabel]);

  const updateSelection = (nextValue) => {
    if (!isControlled) setUncontrolledValue(nextValue);
    onValueChange?.(nextValue);
  };

  const handleInputChange = (event) => {
    const nextLabel = event.target.value;
    setInputValue(nextLabel);
    if (!nextLabel.trim()) {
      updateSelection("");
      return;
    }
    const exactMatch = items.find((item) => item.label.toLocaleLowerCase() === nextLabel.trim().toLocaleLowerCase());
    if (exactMatch && !exactMatch.disabled) updateSelection(exactMatch.value);
  };

  return (
    <div className="relative w-full">
      <input
        id={triggerProps.id}
        type="text"
        list={listId}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled || triggerProps.disabled}
        required={required || triggerProps.required}
        aria-label={triggerProps["aria-label"]}
        aria-required={required || triggerProps.required || undefined}
        data-testid={triggerProps["data-testid"]}
        title={triggerProps.title}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          triggerProps.className
        )}
      />
      {name && <input type="hidden" name={name} value={selectedValue} />}
      <datalist id={listId}>
        {items.map((item, index) => (
          <option key={`${item.value}-${index}`} value={item.label} disabled={item.disabled} />
        ))}
      </datalist>
    </div>
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};